import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useState } from 'react';
import { sum, omit } from 'lodash-es';
import { useT, hasValue, EComponentBaseType } from '@progress-fe/core';
import { Box, Center, Menu, Flex, Grid, Button, GridItem, IconButton, MenuButton, MenuDivider, MenuItem, MenuList } from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import { Dialog, Svg } from '../../base';
import { ComponentsLists } from '../../lists';
import { Input, InputNumber, Select } from '../../inputs';
import { useJsFieldFormData } from '../../../hooks';
import { JsFormConfigContext } from '../../../contexts';
import { JsFieldName, JsFieldError, JsFieldInspector } from '../../jsFormBase';
/**
 * Component renders "/schemas/jsf-flow-composition-init-approx" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is an instance of TFormData.
 */
const FlowCompositionInitApproxJsFieldFC = (props) => {
    const [isDialog, setIsDialog] = useState(false);
    const { components, addToFavorites, removeFromFavorites, runCallback, isInspector } = useContext(JsFormConfigContext) || {};
    const { favorites = [], pureList = [], pseudoList = [], dictionary = {} } = components || {};
    const { t } = useT();
    const formData = useMemo(() => hasValue(props.formData) ? props.formData : null, [props.formData]);
    const { fieldConfig, fieldValues, onChangeFieldValues, onChangeFieldConfig } = useJsFieldFormData({
        config: formData?.fieldConfig || null,
        values: formData?.fieldValues || null,
        onChange: props.onChange
    });
    useEffect(() => {
        onChangeFieldConfig(formData?.fieldConfig);
    }, [formData?.fieldConfig, onChangeFieldConfig]);
    const getComponentNameByUuid = (uuid) => {
        return dictionary[uuid]?.name ?? pseudoList.find((i) => i.uuid === uuid)?.name ?? '';
    };
    const selectedComponentsIds = useMemo(() => (fieldValues ? Object.keys(fieldValues.value) : []), [fieldValues]);
    const selectedOption = useMemo(() => {
        return fieldConfig?.options.find((opt) => opt.value === fieldValues?.optionValue);
    }, [fieldConfig, fieldValues]);
    const hasComponents = !!selectedComponentsIds.length;
    const selectedComponentsAmount = selectedComponentsIds.length;
    const isFieldInvalid = !!props.rawErrors || selectedComponentsAmount !== selectedComponentsIds.length;
    const sumOfComponents = useMemo(() => {
        if (!fieldValues) {
            return undefined;
        }
        const valuesArray = Object.values(fieldValues.value).map((item) => item.value);
        if (valuesArray.every((v) => v === null)) {
            return undefined;
        }
        return sum(valuesArray.map((i) => (hasValue(i) ? Number(i) : undefined)));
    }, [fieldValues]);
    const onChangeFormOptionValue = (option) => {
        if (fieldValues) {
            onChangeFieldValues({ ...fieldValues, optionValue: option.value });
        }
    };
    const onChangeFormValue = (value) => {
        if (fieldValues) {
            onChangeFieldValues({ ...fieldValues, value: value });
        }
    };
    const handleAddComponent = (key) => {
        if (!fieldValues)
            return;
        const initValue = { value: null };
        if (fieldConfig?.tolerance) {
            initValue.tolerance = null;
        }
        onChangeFormValue({ ...fieldValues.value, [key]: initValue });
    };
    const handleChangeValue = (key, value, property) => {
        if (!fieldValues)
            return;
        onChangeFormValue({
            ...fieldValues.value,
            [key]: { ...fieldValues.value[key], [property]: value }
        });
    };
    const handleDeleteComponent = (key) => {
        if (!fieldValues)
            return;
        onChangeFormValue({ ...omit(fieldValues.value, [key]) });
    };
    const handleClearValues = () => {
        if (!fieldValues)
            return;
        const existingKeys = Object.keys(fieldValues.value);
        const resetedValue = { value: null };
        if (fieldConfig?.tolerance) {
            resetedValue.tolerance = null;
        }
        let newValue = {};
        existingKeys.forEach((key) => {
            newValue = { ...newValue, [key]: resetedValue };
        });
        onChangeFormValue(newValue);
    };
    const handleNormalizeCallback = async () => {
        const formDataResult = await runCallback?.(fieldConfig?.callbackCode || '');
        if (hasValue(formDataResult)) {
            onChangeFieldConfig(formDataResult.fieldConfig);
            onChangeFieldValues(formDataResult.fieldValues);
        }
    };
    const tabs = useMemo(() => {
        return [
            {
                type: null,
                label: t(`common.favorites`),
                components: favorites
            },
            {
                type: EComponentBaseType.PURE,
                label: t(`enum.componentBaseType.${EComponentBaseType.PURE}`),
                components: pureList
            },
            {
                type: EComponentBaseType.HYPOTHETICAL,
                label: t(`enum.componentBaseType.${EComponentBaseType.HYPOTHETICAL}`),
                components: []
            },
            {
                type: EComponentBaseType.PSEUDO,
                label: t(`enum.componentBaseType.${EComponentBaseType.PSEUDO}`),
                components: pseudoList
            }
        ];
    }, [favorites, pureList, pseudoList, t]);
    if (!fieldConfig || !fieldValues) {
        return _jsx(JsFieldError, { propName: props.name });
    }
    const gridTemplate = fieldConfig.tolerance ? '176px 1fr 1fr 24px' : '176px 1fr 24px';
    return (_jsx(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: _jsxs(Box, { "data-testid": "FlowCompositionInitApproxJsField-test", "data-name": props.name, children: [_jsxs(Flex, { p: "4px 0 2px 0", flexDirection: "column", gap: "8px", children: [_jsxs(Grid, { gridTemplateColumns: `minmax(0, 184px) 1fr`, alignItems: 'center', children: [_jsx(JsFieldName, { name: fieldConfig.title }), _jsx(Select, { name: props.name, value: selectedOption, options: fieldConfig.options, placeholder: t('common.notSelected'), onChange: (newValue) => {
                                        onChangeFormOptionValue(newValue);
                                    } })] }), hasComponents ? (_jsxs(GridItem, { p: "4px", borderRadius: "4px", border: "1px solid", borderColor: isFieldInvalid ? 'error' : 'border', children: [_jsxs(Grid, { gap: "4px", p: "0 0 4px 0", alignItems: "center", borderBottom: "1px solid", borderColor: "border", templateColumns: gridTemplate, children: [_jsx(GridItem, { children: t('fields.component') }), _jsx(GridItem, { children: t('fields.amount') }), fieldConfig.tolerance && _jsx(GridItem, { children: t('fields.tolerance') }), _jsx(GridItem, { children: _jsx(IconButton, { size: "smSquare", "aria-label": "", variant: "ghostTr", icon: _jsx(Svg, { size: "s12", name: "Cross" }), isDisabled: props.schema.readOnly, onClick: () => handleClearValues() }) })] }), _jsx(Box, { p: "2px 0", children: selectedComponentsIds.map((uuid) => (_jsxs(Grid, { gap: "4px", p: "2px 0", templateColumns: gridTemplate, children: [_jsx(Flex, { align: "center", className: 'cut-text', children: getComponentNameByUuid(uuid) }), _jsx(InputNumber, { isFloat: true, min: 0, size: "xs", variant: "outline", sx: { width: '100%' }, isOnChangeOnlyOnBlur: true, disabled: props.schema.readOnly, rightElement: selectedOption?.unit, isInvalid: !hasValue(fieldValues.value[uuid]?.value), value: fieldValues.value[uuid]?.value || undefined, onChange: (value) => handleChangeValue(uuid, value, 'value') }), fieldConfig.tolerance && (_jsx(InputNumber, { isFloat: true, min: 0, size: "xs", variant: "outline", sx: { width: '100%' }, isOnChangeOnlyOnBlur: true, disabled: props.schema.readOnly, rightElement: fieldConfig.tolerance.unit, isInvalid: !hasValue(fieldValues.value[uuid]?.tolerance), value: fieldValues.value[uuid]?.tolerance || undefined, onChange: (value) => handleChangeValue(uuid, value, 'tolerance') })), _jsx(IconButton, { size: "smSquare", "aria-label": "", variant: "ghost", isDisabled: props.schema.readOnly, icon: _jsx(Svg, { size: "s12", name: "Cross" }), onClick: () => handleDeleteComponent(uuid) })] }, uuid))) }), _jsxs(Grid, { gap: "4px", p: "4px 0 0 0", borderTop: "1px solid", borderColor: "border", templateColumns: "176px 1fr auto", children: [_jsx(Flex, { align: "center", children: t('common.total') }), _jsx(Input, { size: "xs", isDisabled: true, variant: "outline", value: sumOfComponents, isInvalid: fieldConfig.normalizable && sumOfComponents !== 1 }), fieldConfig.normalizable && (_jsx(Button, { size: "xs", variant: "ghost", isDisabled: props.schema.readOnly, onClick: handleNormalizeCallback, children: t('actions.normalize') }))] })] })) : (_jsx(Center, { h: "32px", color: "bodyText", border: "1px solid", borderColor: isFieldInvalid ? 'error' : 'border', borderRadius: "4px", children: _jsx(Box, { opacity: "0.6", children: t('components.no') }) })), !props.schema.readOnly && (_jsx(GridItem, { children: _jsxs(Menu, { matchWidth: true, offset: [0, 2], variant: "outline", children: [_jsx(MenuButton, { size: "xs", as: Button, width: "100%", variant: "ghost", onClick: () => setIsDialog(false), children: t('actions.add') }), _jsxs(MenuList, { motionProps: { animate: false }, zIndex: 2, children: [favorites.map((item) => (_jsx(MenuItem, { isDisabled: !!fieldValues.value[item.uuid], onClick: () => handleAddComponent(item.uuid), children: item.name }, item.uuid))), favorites.length === 0 && (_jsx(MenuItem, { isDisabled: true, children: t('components.noFavorite') })), _jsx(MenuDivider, {}), _jsx(MenuItem, { onClick: () => setIsDialog(true), children: t('components.all') })] })] }) }))] }), isDialog && (_jsx(Dialog, { title: t('components.add'), onClose: () => setIsDialog(false), children: _jsx(GridItem, { pt: "8px", children: _jsx(ComponentsLists, { tabs: tabs, tableSx: { height: 500 }, selectedComponentsIds: Object.keys(fieldValues.value), onAddComponent: handleAddComponent, onDeleteComponent: handleDeleteComponent, onAddToFavorite: (item) => addToFavorites?.([item]), onDeleteFromFavorite: (item) => removeFromFavorites?.([item]), onClose: () => setIsDialog(false) }) }) }))] }) }));
};
export const FlowCompositionInitApproxJsField = observer(FlowCompositionInitApproxJsFieldFC);
//# sourceMappingURL=FlowCompositionInitApproxJsField.js.map